import React, { useRef } from "react";
import styled from "styled-components";
import Checkbox from "../checkbox";
import colors from "../../design/colors";
import fonts from "../../design/typography";
import { ChevronDown } from "../icons/chevron";
import { useClickOutsideCloseModal } from "../../hooks/useClickOutsideCloseElement";

/* **********************************
 * TYPES
 * 타입 정의
 ********************************** */

interface TableColumn<T> {
  key: keyof T;
  header: React.ReactNode;
  render?: (item: T, index: number) => React.ReactNode;
  width?: string;
  align?: "left" | "center" | "right";
  filterOptions?: string[];
  onFilterSelect?: (value: string) => void;
  selectedFilter?: string;
  showPagination?: boolean; // 페이지네이션 옵셔닝
  style?: React.CSSProperties; // 컬럼 스타일
}

interface BasicTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  rowKey: keyof T;

  // 선택 관련
  showCheckbox?: boolean;
  selectedItems?: T[];
  onSelect?: (selected: T[]) => void;

  // 페이지네이션 관련
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;

  // 기타
  observerRef?: React.RefObject<HTMLDivElement>;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;

  showPagination?: boolean; // 페이지네이션 옵셔닝
  // 높이 조정 옵셔닝
  autoHeight?: boolean; // true: 높이 자동, false: 높이 고정
  rowCursorPointer?: boolean; // 행 클릭 시 커서 포인터 옵셔닝
}

/* **********************************
 * 필터 드롭다운
 ********************************** */
const FilterDropdown = ({
  label,
  options,
  selected,
  onSelect,
}: {
  label: string;
  options: string[];
  selected: string;
  onSelect: (val: string) => void;
}) => {
  const [open, setOpen] = React.useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 닫기
  /* **********************************
   * Hooks
   ********************************** */
  useClickOutsideCloseModal([dropdownRef], () => setOpen(false));

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
          background: "none",
          border: "none",
          cursor: "pointer",
          fontWeight: 500,
          fontSize: fonts.label4Medium.fontSize,
          fontFamily: fonts.label4Medium.fontFamily,
          lineHeight: fonts.label4Medium.lineHeight,
          color: colors.gray500,
          width: "100%",
          height: "28px",
        }}
      >
        {selected || label}
        <ChevronDown />
      </button>

      {open && (
        <ul
          style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-60%)",
            zIndex: 10,
            backgroundColor: "white",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            borderRadius: "12px",
            padding: "7px 0",
            minWidth: "fit-content",
          }}
        >
          {options.map((opt) => (
            <li
              key={opt}
              onClick={() => {
                onSelect(opt);
                setOpen(false);
              }}
              style={{
                padding: "8px 16px",
                color: selected === opt ? colors.primary : colors.gray700,
                fontWeight: selected === opt ? 600 : 400,
                cursor: "pointer",
                backgroundColor:
                  selected === opt ? colors.gray50 : "transparent",
              }}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* **********************************
 * 테이블
 ********************************** */
function BasicTable<T>({
  data,
  columns,
  showCheckbox = false,
  selectedItems = [],
  onSelect,
  observerRef,
  rowKey,
  scrollContainerRef,
  emptyMessage,
  currentPage,
  totalPages,
  onPageChange,
  onRowClick,
  showPagination = false,
  autoHeight = false,
  rowCursorPointer = false,
}: BasicTableProps<T>) {
  const isAllSelected =
    data.length > 0 &&
    data.every((item) =>
      selectedItems.some((selected) => selected[rowKey] === item[rowKey])
    );

  const toggleAll = () => {
    if (!onSelect) return;
    const updated = isAllSelected ? [] : data;
    onSelect(updated);
  };

  const toggleItem = (item: T) => {
    if (!onSelect) return;
    const already = selectedItems.some(
      (selected) => selected[rowKey] === item[rowKey]
    );
    const updated = already
      ? selectedItems.filter((s) => s[rowKey] !== item[rowKey])
      : [...selectedItems, item];
    onSelect(updated);
  };

  return (
    <TableContainer $dataLength={data.length} $autoHeight={autoHeight}>
      <TableWrapper
        ref={scrollContainerRef}
        $dataLength={data.length}
        $autoHeight={autoHeight}
      >
        <StyledTable
          $showCheckbox={showCheckbox}
          $rowCursorPointer={rowCursorPointer}
        >
          <thead>
            <tr>
              {showCheckbox && (
                <th
                  style={{ width: "64px", textAlign: "center", height: "40px" }}
                >
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Checkbox
                      checked={isAllSelected}
                      setChecked={toggleAll}
                      size="16px"
                      checkIconSize="24px"
                    />
                  </div>
                </th>
              )}
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  style={{
                    width: col.width ?? "auto",
                    textAlign: col.align ?? "center",
                    fontSize: fonts.label4Medium.fontSize,
                    fontFamily: fonts.label4Medium.fontFamily,
                    lineHeight: fonts.label4Medium.lineHeight,
                  }}
                >
                  {col.filterOptions ? (
                    <FilterDropdown
                      label={col.header as string}
                      options={col.filterOptions}
                      selected={col.selectedFilter || ""}
                      onSelect={col.onFilterSelect || (() => {})}
                    />
                  ) : (
                    col.header
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr style={{ height: "100%" }}>
                <td colSpan={columns.length + (showCheckbox ? 1 : 0)}>
                  <div
                    style={{
                      padding: "32px 0",
                      textAlign: "center",
                      color: colors.gray400,
                      height: "calc(100vh - 390px)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: fonts.body3Regular.fontSize,
                    }}
                  >
                    {emptyMessage}
                  </div>
                </td>
              </tr>
            ) : (
              data.map((item, idx) => (
                <tr
                  key={String(item[rowKey])}
                  onClick={() => onRowClick?.(item)}
                >
                  {showCheckbox && (
                    <td
                      style={{ textAlign: "center" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        <Checkbox
                          checked={selectedItems.some(
                            (s) => s[rowKey] === item[rowKey]
                          )}
                          setChecked={() => {
                            toggleItem(item);
                          }}
                          size="16px"
                          checkIconSize="24px"
                        />
                      </div>
                    </td>
                  )}
                  {columns.map((col, colIdx) => (
                    <td key={colIdx} style={col.style}>
                      {col.render
                        ? col.render(item, idx)
                        : (item[col.key] as any)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </StyledTable>
        {observerRef && <div ref={observerRef} style={{ height: "1px" }} />}
      </TableWrapper>
      {showPagination && (
        <PaginationWrapper>
          <button
            onClick={() => onPageChange?.(1)}
            disabled={!totalPages || currentPage === 1}
          >
            &lt;&lt;
          </button>
          <button
            onClick={() => onPageChange?.(currentPage! - 1)}
            disabled={!totalPages || currentPage === 1}
          >
            &lt;
          </button>

          {totalPages ? (
            Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((page) => {
                if (totalPages <= 3) return true;
                if (currentPage === 1) return page <= 3;
                if (currentPage === totalPages) return page > totalPages - 3;
                return Math.abs(currentPage! - page) <= 1;
              })
              .map((page) => (
                <PageButton
                  key={page}
                  onClick={() => onPageChange?.(page)}
                  $active={page === currentPage}
                  data-page
                >
                  {page}
                </PageButton>
              ))
          ) : (
            <PageButton $active={true} data-page>
              1
            </PageButton>
          )}

          <button
            onClick={() => onPageChange?.(currentPage! + 1)}
            disabled={!totalPages || currentPage === totalPages}
          >
            &gt;
          </button>
          <button
            onClick={() => onPageChange?.(totalPages || 1)}
            disabled={!totalPages || currentPage === totalPages}
          >
            &gt;&gt;
          </button>
        </PaginationWrapper>
      )}
    </TableContainer>
  );
}

export default BasicTable;

// 스타일 정의
const TableContainer = styled.div<{
  $dataLength: number;
  $autoHeight: boolean;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  background-color: ${colors.white};
  border-radius: 8px;
  border: 1px solid ${colors.gray200};
  min-height: ${({ $dataLength, $autoHeight }) =>
    $autoHeight ? "auto" : $dataLength > 0 ? "calc(100vh - 335px)" : "auto"};
`;

const TableWrapper = styled.div<{
  $dataLength: number;
  $autoHeight: boolean;
}>`
  flex-grow: 1;
  width: 100%;
  min-width: 0;
  min-height: ${({ $dataLength, $autoHeight }) =>
    $autoHeight ? "auto" : $dataLength > 0 ? "calc(100vh - 335px)" : "auto"};
  max-height: ${({ $dataLength, $autoHeight }) =>
    $autoHeight ? "none" : $dataLength > 0 ? "calc(100vh - 335px)" : "auto"};
  overflow-y: ${({ $dataLength }) => ($dataLength > 0 ? "auto" : "hidden")};
  overflow-x: auto;
  position: relative;
  border-radius: 8px;

  ${({ theme }) => theme.device.mobile} {
    max-height: ${({ $dataLength, $autoHeight }) =>
      $autoHeight ? "auto" : $dataLength > 0 ? "calc(100vh - 330px)" : "auto"};
    min-height: ${({ $dataLength, $autoHeight }) =>
      $autoHeight ? "auto" : $dataLength > 0 ? "calc(100vh - 330px)" : "auto"};
    overflow-x: auto;
  }
`;

const StyledTable = styled.table<{
  $showCheckbox?: boolean;
  $rowCursorPointer?: boolean;
}>`
  width: max-content;
  min-width: 100%;
  min-height: 100%;
  table-layout: auto;
  border-collapse: collapse;
  font-size: ${fonts.label4Medium.fontSize};
  font-family: ${fonts.label4Medium.fontFamily};
  line-height: ${fonts.label4Medium.lineHeight};

  thead {
    background-color: ${colors.gray100};
    color: ${colors.gray500};
    position: sticky;
    top: 0;
    z-index: 1;
    border-bottom: 1px solid ${colors.gray400};
  }

  th,
  td {
    text-align: center;
    vertical-align: middle;
    padding: 6px 10px;
    white-space: nowrap;
    border-right: 1px solid ${colors.gray200};
  }

  th:last-child,
  td:last-child {
    border-right: none;
  }

  th {
    font-weight: 500;
    height: 40px;
    background-color: ${colors.gray100};
  }

  td {
    border-bottom: 1px solid ${colors.gray100};
    color: ${colors.gray900};
  }

  tbody tr:hover td {
    background-color: ${colors.tableHover};
    cursor: ${({ $showCheckbox, $rowCursorPointer }) =>
      $rowCursorPointer ? "pointer" : $showCheckbox ? "default" : "default"};
  }

  // 호버 시 info-badge 배경색 변경
  tbody tr:hover td .info-badge {
    background-color: ${colors.white};
  }

  ${({ theme }) => theme.device.mobile} {
    th,
    td {
      padding: 2px 6px;
      font-size: ${fonts.label5Regular.fontSize} !important;
      line-height: ${fonts.label5Regular.lineHeight} !important;
    }

    th {
      height: 28px; // 헤더 셀 높이 축소
      font-size: ${fonts.label5Medium.fontSize};
    }
  }
`;

const InfoBadge = styled.div`
  display: inline-block;
  padding: 7px 16px;
  border-radius: 12px;
  background-color: ${colors.gray50};
  color: ${colors.gray900};
  white-space: nowrap;
  min-width: max-content;
  box-sizing: border-box;
`;

// 페이지네이션
const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid ${colors.gray100};

  button:not([data-page]) {
    background: none;
    border: none;
    color: ${colors.gray500};
    cursor: pointer;
    font-size: 14px;
    padding: 6px 10px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
      color: ${colors.gray300};
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      color: ${colors.primary};
    }
  }
`;

const PageButton = styled.button<{ $active?: boolean }>`
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  background-color: ${({ $active }) =>
    $active ? colors.blue500 : "transparent"};
  color: ${({ $active }) => ($active ? colors.white : colors.gray700)};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${({ $active }) =>
      $active ? colors.primary : colors.gray100};
  }

  &:disabled {
    color: ${colors.gray300};
    cursor: not-allowed;
    background-color: transparent;
  }
`;
